/**
 * Image Utilities
 * Helpers for optimized image loading with multiple size variants
 *
 * Image sizes generated by Azure Function:
 * - Thumbnail: 200x200 (~20KB) - for table display
 * - Preview: 800x800 (~200KB) - for modal review
 * - Full: Original (~2-3MB) - for download
 */

/**
 * Get thumbnail URL from original image URL
 * Thumbnails (200x200) are generated by Azure Function and have "-thumb" suffix
 *
 * @param {string} imageUrl - Original image URL
 * @returns {string} - Thumbnail URL
 *
 * @example
 * getThumbnailUrl('https://assets.bysnaps.ai/output/temp/image.png')
 * // Returns: 'https://assets.bysnaps.ai/output/temp/image-thumb.png'
 */
function getBrowserAccessibleUrl(imageUrl) {
  return imageUrl.replace(
    /^http:\/\/snaps-montage-api-local:8080(?=\/files\/)/i,
    'http://127.0.0.1:18080'
  );
}

function getVariantUrl(imageUrl, suffix) {
  if (!imageUrl || typeof imageUrl !== 'string') {
    return imageUrl;
  }

  const browserUrl = getBrowserAccessibleUrl(imageUrl);
  if (browserUrl !== imageUrl) return browserUrl;

  const queryIndex = browserUrl.search(/[?#]/);
  const pathname = queryIndex === -1 ? browserUrl : browserUrl.slice(0, queryIndex);
  const query = queryIndex === -1 ? '' : browserUrl.slice(queryIndex);
  if (/[?&](?:idrive|exp|sig)=/.test(query)) return imageUrl;

  const extensionMatch = pathname.match(/\.(png|jpg|jpeg)$/i);
  if (!extensionMatch) return imageUrl;

  const extension = extensionMatch[0];
  return `${pathname.slice(0, -extension.length)}${suffix}${extension}${query}`;
}

export function getThumbnailUrl(imageUrl) {
  return getVariantUrl(imageUrl, '-thumb');
}

/**
 * Get preview URL from original image URL
 * Previews (800x800) are generated by Azure Function and have "-preview" suffix
 *
 * @param {string} imageUrl - Original image URL
 * @returns {string} - Preview URL
 *
 * @example
 * getPreviewUrl('https://assets.bysnaps.ai/output/temp/image.png')
 * // Returns: 'https://assets.bysnaps.ai/output/temp/image-preview.png'
 */
export function getPreviewUrl(imageUrl) {
  return getVariantUrl(imageUrl, '-preview');
}

/**
 * Get optimized image URL for display context
 * Returns appropriate size based on usage:
 * - thumbnail: 200x200 for table display
 * - preview: 800x800 for modal review
 * - full: Original for download
 *
 * @param {string} imageUrl - Original image URL
 * @param {string} size - Display size context: 'thumbnail' | 'preview' | 'full'
 * @returns {string} - Optimized image URL
 */
export function getOptimizedImageUrl(imageUrl, size = 'full') {
  if (!imageUrl) {
    return imageUrl;
  }

  switch (size) {
    case 'thumbnail':
      return getThumbnailUrl(imageUrl);
    case 'preview':
      return getPreviewUrl(imageUrl);
    case 'full':
    default:
      return imageUrl;
  }
}
